import { useEffect, useRef, type Dispatch, type SetStateAction } from 'react';
import { loadDraft, saveDraft } from '../utils/inspectionReportDraftStorage';
import { initialFormValues, type InspectionFormValues } from '../utils/inspectionReportFormDefaults';
import type { HvacSystemValues } from '../components/HvacSystemFields';
import { defaultHvacSystem } from '../components/HvacSystemFields';
import type { PlumbingSystemValues } from '../components/PlumbingSystemFields';
import { defaultPlumbingSystem } from '../components/PlumbingSystemFields';

const PERSIST_DEBOUNCE_MS = 400;

function parsePrefill(snapshot: string): Partial<InspectionFormValues> {
  try {
    return JSON.parse(snapshot) as Partial<InspectionFormValues>;
  } catch {
    return {};
  }
}

interface UseInspectionReportDraftParams {
  homeInspectionJobId: string | undefined;
  /** Stable serialized prefill from `JSON.stringify(prefillData ?? {})` to avoid effect churn from new object identity. */
  prefillSnapshot: string;
  formValues: InspectionFormValues;
  hvacSystems: HvacSystemValues[];
  plumbingSystems: PlumbingSystemValues[];
  dirty: boolean;
  setFormValues: Dispatch<SetStateAction<InspectionFormValues>>;
  setHvacSystems: Dispatch<SetStateAction<HvacSystemValues[]>>;
  setPlumbingSystems: Dispatch<SetStateAction<PlumbingSystemValues[]>>;
  setDirty: Dispatch<SetStateAction<boolean>>;
  setRestoredFromDraft: Dispatch<SetStateAction<boolean>>;
  isPending: boolean;
}

/**
 * Local draft persistence (debounced), flush on pagehide, and navigation guard on beforeunload.
 * stateRef holds latest state so unload handlers do not close over stale values.
 */
export function useInspectionReportDraft({
  homeInspectionJobId,
  prefillSnapshot,
  formValues,
  hvacSystems,
  plumbingSystems,
  dirty,
  setFormValues,
  setHvacSystems,
  setPlumbingSystems,
  setDirty,
  setRestoredFromDraft,
  isPending,
}: UseInspectionReportDraftParams) {
  const stateRef = useRef({
    formValues,
    hvacSystems,
    plumbingSystems,
    dirty,
    homeInspectionJobId,
  });
  const isPendingRef = useRef(false);

  useEffect(() => {
    isPendingRef.current = isPending;
  }, [isPending]);

  useEffect(() => {
    stateRef.current = { formValues, hvacSystems, plumbingSystems, dirty, homeInspectionJobId };
  }, [formValues, hvacSystems, plumbingSystems, dirty, homeInspectionJobId]);

  useEffect(() => {
    const prefill = parsePrefill(prefillSnapshot);
    const hasPrefill = Object.keys(prefill).length > 0;

    if (!homeInspectionJobId) {
      setRestoredFromDraft(false);
      if (hasPrefill) {
        setFormValues((prev) => ({ ...prev, ...prefill }));
      }
      return;
    }

    const draft = loadDraft(homeInspectionJobId);
    if (draft) {
      setFormValues({ ...initialFormValues, ...(draft.formValues as Partial<InspectionFormValues>) });
      setHvacSystems(draft.hvacSystems.length > 0 ? draft.hvacSystems : [{ ...defaultHvacSystem }]);
      setPlumbingSystems(draft.plumbingSystems.length > 0 ? draft.plumbingSystems : [{ ...defaultPlumbingSystem }]);
      setRestoredFromDraft(true);
      setDirty(true);
    } else {
      setRestoredFromDraft(false);
      if (hasPrefill) {
        setFormValues((prev) => ({ ...prev, ...prefill }));
      }
    }
  }, [homeInspectionJobId, prefillSnapshot, setFormValues, setHvacSystems, setPlumbingSystems, setDirty, setRestoredFromDraft]);

  useEffect(() => {
    if (!homeInspectionJobId || !dirty) return;
    const timer = window.setTimeout(() => {
      saveDraft(homeInspectionJobId, {
        formValues: { ...formValues } as Record<string, string | boolean>,
        hvacSystems,
        plumbingSystems,
        savedAt: new Date().toISOString(),
      });
    }, PERSIST_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [formValues, hvacSystems, plumbingSystems, homeInspectionJobId, dirty]);

  useEffect(() => {
    const flushDraft = () => {
      const s = stateRef.current;
      if (s.homeInspectionJobId && s.dirty) {
        saveDraft(s.homeInspectionJobId, {
          formValues: { ...s.formValues } as Record<string, string | boolean>,
          hvacSystems: s.hvacSystems,
          plumbingSystems: s.plumbingSystems,
          savedAt: new Date().toISOString(),
        });
      }
    };
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      flushDraft();
      const s = stateRef.current;
      if (s.dirty && !isPendingRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('pagehide', flushDraft);
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('pagehide', flushDraft);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);
}
